import { useQuery } from '@urql/vue';
import { uniqBy } from 'es-toolkit';
import {
    ViewProductOutletDetailsDocument,
    type ViewProductDetailsQuery,
    type ViewProductOutletDetailsQuery,
    type ViewProductOutletDetailsQueryVariables,
} from '~/graphql';

type ProductDetails = Extract<ViewProductDetailsQuery['product'], { __typename: 'SimpleProduct' }>;

export interface OutletData {
    id: string
    title: string
    availableQuantity: number
    modelQuantity: number
}

type ProductOutletStockInfo = ViewProductOutletDetailsQuery['products']['edges'][0]['node'][];

/** Compute a product's effective stocks to be shown on the product page by taking our special stock count grouping attribute into account - see more in comments  */
export function useOutletStocks(productDetails: ComputedRef<ProductDetails | undefined>) {
    // We have a "special" attribute (per-store), which if set on a product causes its stock calculation to be handled differently. Basically, if two or more products have the same value in their special attribute, their stocks should all count towards the total stocks of the product (per outlet) - they are essentially grouped together to kind of "be the same product", at least when concerning stock count calculations.
    const {
        public: {
            outletGroupingAttributeId,
        },
    } = useRuntimeConfig();

    const outletBundleAttributes = computed(() => productDetails.value?.outletBundleAttributes || []);

    const attributesData = computed(() => (
        outletBundleAttributes.value.map((attributeValue) => useAttributeValue(attributeValue))
    ));

    // Updated in a watcher to fetch more outlet data
    const outletCursor = ref<string>();

    // Here we finally find the product's (possible) attribute value for our special stock grouping attribute
    const groupingAttribute = computed(() => attributesData.value.find((a) => a.id === outletGroupingAttributeId));

    const outletQueryVariables = computed(() => ({
        after: outletCursor.value,
        attributeId: outletGroupingAttributeId,
        bundle: groupingAttribute.value?.value,
    }));

    // This contains the quantities of all the related products whose quantities should be bundled with this one (per product per outlet). This is because products with the same grouping attribute value should all count towards the same total quantity, as explained earlier.
    // Updated in a watcher
    const outletsResult = ref<ProductOutletStockInfo>([]);

    // This aggregates data into `outletsResult` when its variables are updated
    const {
        data: outletData,
        fetching,
    } = useQuery<ViewProductOutletDetailsQuery, ViewProductOutletDetailsQueryVariables>({
        query: ViewProductOutletDetailsDocument,
        variables: outletQueryVariables,
        pause: computed(() => !groupingAttribute.value?.value || outletGroupingAttributeId === undefined),
        requestPolicy: 'network-only',
    });

    // Check the current value of outlet data, push it to the outlet result and then trigger fetching the next page of outlet data until everything is fetched.
    watch(outletData, (value) => {
        outletsResult.value = uniqBy(
            [
                ...outletsResult.value,
                ...(value?.products?.edges.map((edge) => edge.node) || []),
            ],
            (item) => item.id,
        );

        if (value?.products?.pageInfo.hasNextPage) {
            outletCursor.value = value.products.pageInfo.endCursor ?? undefined;
        }
    }, { immediate: true });

    // This aggregates the full outletsResult into a list of the bundled products' stock info per each outlet. These can be shown on the product's page as its stocks.
    const outlets = computed(() => {
        // First calculate the base product's stocks for each outlet
        const result = productDetails.value
            ? productDetails.value?.outlets.map<OutletData>((productOutlet) => ({
                id: productOutlet.outlet.id,
                title: productOutlet.outlet.title,
                availableQuantity: productOutlet.availableQuantity,
                modelQuantity: productOutlet.modelQuantity,
            }))
            : [];

        // Then go through the *other* grouped products and add their outlet stocks into the result
        outletsResult.value
            .filter((item) => item.id !== productDetails.value?.id)
            .forEach((item) => {
                const outletsData = item.__typename === 'SimpleProduct' ? item.outlets : [];

                outletsData.forEach((object) => {
                    let outlet = result.find((o) => o.id === object.outlet.id);

                    if (!outlet) {
                        outlet = {
                            id: object.outlet.id,
                            title: object.outlet.title,
                            availableQuantity: 0,
                            modelQuantity: 0,
                        };

                        result.push(outlet);
                    }

                    outlet.modelQuantity += object.modelQuantity;
                });
            });

        result.sort((a, b) => a.title.localeCompare(b.title));

        return result;
    });

    return {
        outlets,
        fetching,
    };
}
